import { useQuery } from '@apollo/client';
import { FileAttachmentsWidget, FileAttachmentsWidgetEditFeature } from '@procureai/ui-modules-mui';
import { useTranslation } from 'react-i18next';

import { FileAttachmentStatus } from '../../FileAttachmentsWidget/FileAttachmentsWidget.types';
import {
  getAttachmentFragments,
  useFileAttachmentsUpdateMutation,
  FILE_ACCEPTS,
  classificationOptions,
} from '../../FileAttachmentsWidget/FileAttachmentsWidget.util';
import { useSourcingCase } from '../../Module.hooks';
import { TsrctAttachmentParentVariant, TsrctUploadedByVariant } from '@/generated/gql/graphql';
import { isSourcingCaseEditable } from '@/legacy/common/guards/sourcingCase.guards';
import { I18nNamespace } from '@/legacy/common/types/I18nNamespace';

import {
  SOURCING_CASE_INTERNAL_FILE_ATTACHMENTS_CASE_AND_DEMAND_QUERY,
  SOURCING_CASE_INTERNAL_FILE_ATTACHMENTS_QUERY,
} from './SourcingCaseInternalFileAttachmentsWidget.gql';

export const SourcingCaseInternalFileAttachmentsWidget = () => {
  const { id } = useSourcingCase();
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'SourcingCaseInternalFileAttachmentsWidget' });
  const sourcingCaseParent = {
    id,
    variant: TsrctAttachmentParentVariant.SOURCING_CASE,
  };

  const { data: caseAndDemandData, loading: caseAndDemandLoading } = useQuery(
    SOURCING_CASE_INTERNAL_FILE_ATTACHMENTS_CASE_AND_DEMAND_QUERY,
    {
      variables: {
        sourcingCaseId: id,
      },
    },
  );

  const variables = {
    parents: [
      sourcingCaseParent,
      {
        variant: TsrctAttachmentParentVariant.CONTRACT_DEMAND,
        id: caseAndDemandData?.tesseract.sourcingCase.demand?.id,
      },
    ],
    filter: {
      uploadedByVariants: [TsrctUploadedByVariant.INTERNAL_USER],
    },
  };

  const { data, loading: attachmentsLoading } = useQuery(SOURCING_CASE_INTERNAL_FILE_ATTACHMENTS_QUERY, {
    variables,
    skip: !caseAndDemandData?.tesseract.sourcingCase.demand?.id,
  });

  const { save, saving } = useFileAttachmentsUpdateMutation({
    refetchQueries: [{ query: SOURCING_CASE_INTERNAL_FILE_ATTACHMENTS_QUERY, variables }],
    parent: sourcingCaseParent,
  });

  const loading = caseAndDemandLoading || attachmentsLoading;

  const attachments = getAttachmentFragments(data?.tesseract.attachments);

  return (
    <>
      <FileAttachmentsWidget
        editFeatures={[FileAttachmentsWidgetEditFeature.UPLOAD, FileAttachmentsWidgetEditFeature.CHANGE_VISIBILITY]}
        uploadButtonLabel={t('upload.buttonLabel')}
        attachments={attachments}
        loading={loading}
        onSave={save}
        saving={saving}
        getItemStatus={(attachment) =>
          attachment.visibilities.some((v) => v.id === id) ? FileAttachmentStatus.VISIBLE : FileAttachmentStatus.HIDDEN
        }
        isItemDeletable={(attachment) => attachment.parent.id === id}
        isEditable={isSourcingCaseEditable(caseAndDemandData?.tesseract.sourcingCase) && !loading}
        accept={FILE_ACCEPTS}
        classificationOptions={classificationOptions}
        getItemClassification={(attachment) => attachment.classification}
      />
    </>
  );
};
